import type { ErrorMessage, StoredDocuments } from '../types'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { mapKeys, snakeCase } from 'lodash'

import { getEffectiveId, getViewingId } from '../utils'
import { isNotProposedDocument } from '../predicates'
import type {
  DirectUploadDocument,
  DocumentUploaderItem,
} from '@app/shared/types/sf-data'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

export const useOCRDocuments = defineStore('OCRDocuments', () => {
  const documents = ref<StoredDocuments>({})
  const maxDocumentNumber = ref<number>(0)
  const uploadProgress = ref<Record<string, string>>({})
  const errorsPerDocument = ref<Record<string, string[]>>({})

  const allDocumentIds = computed(() => {
    return Object.keys(documents.value)
  })

  const allDocumentValues = computed(() => {
    return Object.values(documents.value)
  })

  const allDocumentsLength = computed(() => allDocumentIds.value.length)

  const allDocumentsErroredOut = computed(() =>
    allDocumentIds.value.every((id) => !!errorsPerDocument.value[id])
  )

  const noFiles = computed(() => allDocumentIds.value.length === 0)
  const reachedMaxFiles = computed(
    () => allDocumentIds.value.length >= maxDocumentNumber.value
  )
  const exceededMaxFiles = computed(
    () => allDocumentIds.value.length > maxDocumentNumber.value
  )

  const nonProposedDocuments = computed(
    () => !!allDocumentValues.value.find(isNotProposedDocument)
  )

  function addDocuments(incomingDocuments: DocumentUploaderItem[]): void {
    incomingDocuments.forEach(addDocument)
  }

  function addDocument(document: DocumentUploaderItem): void {
    const effectiveId = getEffectiveId(document)

    if (!effectiveId) {
      return
    }

    const viewingId = getViewingId(document)

    documents.value = {
      ...documents.value,
      [effectiveId]: Object.freeze({ ...document, id: effectiveId, viewingId }),
    }
  }

  function receiveDocuments(incomingDocuments: StoredDocuments): void {
    documents.value = incomingDocuments
  }

  function deleteDocument(documentId: string): void {
    delete documents.value[documentId]
    delete errorsPerDocument.value[documentId]
    delete uploadProgress.value[documentId]
  }

  function updateUploadProgress({
    uuid,
    file,
    progress,
  }: {
    uuid: string
    file: DirectUploadDocument
    progress: string
  }): void {
    uploadProgress.value = {
      ...uploadProgress.value,
      [uuid]: progress,
    }

    if (!documents.value[uuid]) {
      addDocument(file)
    }
  }

  function addDocumentError(error: ErrorMessage): boolean {
    const docId = Object.keys(documents.value).find(
      (id) =>
        documents.value[id].document_id === error.documentId ||
        documents.value[id].name === error.filename
    )

    if (!docId) {
      return false
    }

    const errorParams = mapKeys(error.params, (_, key) => snakeCase(key))

    errorsPerDocument.value = {
      ...errorsPerDocument.value,
      [docId]: [
        ...(errorsPerDocument.value[docId] || []),
        I18n.t(error.key, errorParams),
      ],
    }

    return true
  }

  function removeDocumentsWithError(): void {
    Object.keys(errorsPerDocument.value).forEach((documentId) => {
      deleteDocument(documentId)
    })
  }

  function resetStore(): void {
    documents.value = {}
    uploadProgress.value = {}
    errorsPerDocument.value = {}
  }

  return {
    addDocument,
    addDocuments,
    receiveDocuments,
    allDocumentIds,
    allDocumentValues,
    allDocumentsLength,
    maxDocumentNumber,
    noFiles,
    reachedMaxFiles,
    exceededMaxFiles,
    allDocumentsErroredOut,
    nonProposedDocuments,
    deleteDocument,
    uploadProgress,
    updateUploadProgress,
    documents,
    errorsPerDocument,
    addDocumentError,
    removeDocumentsWithError,
    resetStore,
  }
})
